import { Container, Title, Stack, Paper, Alert, Group, Text } from '@mantine/core';
import { useState, useMemo, useEffect } from 'react';
import { IconAlertCircle } from '@tabler/icons-react';
import { DataTable } from '@/features/dataEditor/DataTable';
import { ParameterSelector } from '@/features/dataEditor/ParameterSelector';
import { SourceFilter } from '@/features/dataEditor/SourceFilter';
import { useQuery } from '@tanstack/react-query';
import { DataEditorResponse } from '@/types/dataEditor';
import {
  getSourcesForParameter,
  getParameterLabel,
} from '@/features/dataEditor/parameterConfig';
import { buildApiUrl } from '@/api/config';
import { CACHE_TIMES } from '@/config/cacheTimes';
import { apiClient } from '@/api/client';
import { api as dataEditorApi } from '@/api/dataEditor';
import { useTank } from '@/contexts/TankContext';
import { NoTankSelected } from '@/components/common/NoTankSelected';
import { useDateRangeParams } from '@/hooks/useDateRangeParams';
import { DateRangeFilter } from '@/features/dataEditor/DateRangeFilter';

async function fetchParameterData(
  parameter: string,
  tankId?: string,
  startDate?: string,
  endDate?: string
): Promise<DataEditorResponse> {
  if (!tankId) {
    throw new Error('Tank ID is required');
  }

  const params = new URLSearchParams({
    parameter,
    // No source = fetch all sources
    exclude_outliers: 'false',
  });

  // Add date range params
  if (startDate) {
    params.set('start_date', startDate);
  }
  if (endDate) {
    params.set('end_date', endDate);
  }

  const response = await apiClient(
    buildApiUrl(`/api/tanks/${tankId}/outliers?${params}`)
  );

  if (!response.ok) {
    throw new Error('Failed to fetch data');
  }

  return response.json();
}

export default function DataEditorPage() {
  const { selectedTank } = useTank();

  // URL-based date range state (Task 111)
  const { dateRange, setDateRange, startDateString, endDateString } = useDateRangeParams();

  // Fetch available parameters for this tank (with actual measurement data)
  // Note: Uses different query key than useTankParameters to avoid cache conflicts
  // useTankParameters returns {key, display_name} for navbar features
  // This endpoint returns {value, label} for the parameter selector dropdown
  const { data: parametersResponse, isLoading: isLoadingParameters } = useQuery({
    queryKey: ['measurement-parameters', selectedTank?.id],
    queryFn: () => dataEditorApi.getTankParameters(selectedTank!.id),
    enabled: !!selectedTank,
    staleTime: CACHE_TIMES.DATA,
  });

  const availableParameters = parametersResponse?.parameters || [];

  // Selected parameter (just the value, not source)
  const [selectedParam, setSelectedParam] = useState<string | null>(null);

  // Set default parameter when parameters are loaded
  useEffect(() => {
    if (!selectedParam && availableParameters.length > 0) {
      const firstParam = availableParameters[0];
      if (firstParam) {
        setSelectedParam(firstParam.value);
      }
    }
  }, [availableParameters, selectedParam]);

  // Selected sources for filtering (empty = all)
  const [selectedSources, setSelectedSources] = useState<string[]>([]);

  // Available sources for the selected parameter (from actual data, not static config)
  const availableSources = useMemo(() => {
    if (!selectedParam || !availableParameters.length) return [];

    // Find the selected parameter and get its sources from the API response
    const param = availableParameters.find((p: any) => p.value === selectedParam);
    if (param?.sources) {
      // API returns sources as [{value, label}], extract just the values for filtering
      return param.sources.map((s: { value: string; label: string }) => s.value);
    }

    // Fallback to static config if sources not in response (backward compat)
    return getSourcesForParameter(selectedParam);
  }, [selectedParam, availableParameters]);

  // Reset source filter when parameter changes
  const handleParameterChange = (param: string | null) => {
    setSelectedParam(param);
    setSelectedSources([]);
  };

  // Fetch data for selected parameter (all sources)
  const { data: response, isLoading, error } = useQuery({
    queryKey: ['outliers', selectedParam, selectedTank?.id, startDateString, endDateString],
    queryFn: () =>
      selectedParam
        ? fetchParameterData(selectedParam, selectedTank?.id, startDateString, endDateString)
        : Promise.resolve({ data: [], count: 0, start_date: '', end_date: '' }),
    enabled: !!selectedParam && !!selectedTank,
    staleTime: CACHE_TIMES.LIVE,
  });

  // Filter data by selected sources (client-side)
  const filteredData = useMemo(() => {
    if (!response?.data) return [];
    if (selectedSources.length === 0) return response.data;
    return response.data.filter(item =>
      selectedSources.includes(item.source)
    );
  }, [response?.data, selectedSources]);

  // Build ParameterOption for DataTable (for backward compat)
  const selectedParameter = useMemo(() => {
    if (!selectedParam) return null;
    const paramConfig = availableParameters.find(p => p.value === selectedParam);
    if (!paramConfig) return null;
    // DataTable expects source info - get from first available source
    const sources = getSourcesForParameter(selectedParam);
    return {
      value: selectedParam,
      label: paramConfig.label,
      group: '', // Not used
      source: sources[0] || 'unknown',
      table: '', // Not used
    };
  }, [selectedParam, availableParameters]);

  // Show message if no tank selected
  if (!selectedTank) {
    return (
      <Container fluid px="xl" py="xl">
        <NoTankSelected purpose="to edit data" />
      </Container>
    );
  }

  return (
    <Container fluid px="xl" py="xl">
      <Stack gap="lg">
        <div>
          <Title order={1}>Data Editor</Title>
          <Text c="dimmed" size="sm">
            Tank: {selectedTank.name}
          </Text>
        </div>

        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error loading data"
            color="red"
          >
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </Alert>
        )}

        <Paper p="md" withBorder>
          <Stack gap="md">
            {/* Parameter Selector + Source Filter + Date Range Filter */}
            <Group align="flex-end" gap="lg">
              <div style={{ width: 300 }}>
                <ParameterSelector
                  value={selectedParam}
                  onChange={handleParameterChange}
                  parameters={availableParameters}
                  isLoading={isLoadingParameters}
                />
              </div>
              <SourceFilter
                availableSources={availableSources}
                selectedSources={selectedSources}
                onChange={setSelectedSources}
              />
              <div style={{ width: 280 }}>
                <DateRangeFilter
                  dateRange={dateRange}
                  onChange={setDateRange}
                />
              </div>
            </Group>

            {/* Data count alert */}
            {selectedParam && (
              <Alert color="blue" variant="light">
                Showing {filteredData.length} of {response?.count || 0} data points
                for {getParameterLabel(selectedParam)}
                {selectedSources.length > 0 && (
                  <> (filtered by source)</>
                )}
              </Alert>
            )}

            {/* Data Table */}
            <DataTable
              data={filteredData}
              isLoading={isLoading}
              selectedParameter={selectedParameter}
              tankId={selectedTank.id}
              startDateString={startDateString}
              endDateString={endDateString}
            />
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}
